import { TaskTemplate } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Toast } from 'widgets';

import { useDeleteTemplate } from '../task-templates-queries';

export interface DeleteTemplateDialogProps {
    template: TaskTemplate;
    open?: boolean;
    onOpenChange: (open: boolean) => void;
    onCancel?: () => void;
}

export const DeleteTemplateDialog = (props: DeleteTemplateDialogProps) => {
    const deleteMutation = useDeleteTemplate();
    const { template } = props;

    const handleConfirm = () => {
        deleteMutation
            .mutateAsync({ taskTemplateGUID: template.taskTemplateGUID })
            .then(() => {
                Toast.success(`Template "${template?.title?.slice(0, 40)}" deleted successfully`);
                props.onOpenChange(false);
            })
            .catch((error) => {
                Toast.forError(error, `Failed to delete template "${template?.title?.slice(0, 40)}"`);
            });
    };

    if (!template) {
        return (
            <Dialog open={props.open} onOpenChange={props.onOpenChange}>
                <DialogContent className="overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Delete Template</DialogTitle>
                        <DialogDescription>Template not found.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={props.onCancel ?? (() => props.onOpenChange(false))}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={props.open} onOpenChange={props.onOpenChange}>
            <DialogContent className="overflow-hidden">
                <DialogHeader>
                    <DialogTitle>Delete Template</DialogTitle>
                    <DialogDescription>Are you sure you want to delete this template?</DialogDescription>
                    <div className="mt-2">
                        <p className="max-w-[300px] truncate font-semibold">{template.title || 'Untitled Template'}</p>
                        <p className="text-muted-foreground mt-1 text-sm">This action cannot be undone.</p>
                    </div>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={props.onCancel ?? (() => props.onOpenChange(false))}
                        disabled={deleteMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button variant="destructive" onClick={handleConfirm} disabled={deleteMutation.isPending}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default DeleteTemplateDialog;
